import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useUtilityData } from "../utilities/contexts";
import {
  Select,
  Input,
  DisclosurePanel,
  DisclosureButton,
  Disclosure,
  Button,
} from "@headlessui/react";
import Image from "next/image";
import {
  IconEmail,
  IconOnline,
  IconPerson,
  IconTelephone,
} from "../../styles/icons";
import {
  Checkbox,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Tooltip,
} from "@chakra-ui/react";
import { AnimatePresence, easeOut, motion } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";
import { MdOutlineClose } from "react-icons/md";
import { useScrollWithShadow } from "../hooks/useScrollWithShadow";
import { SearchItemTitle } from "./SearchItemTitle";
import { LocationSearch } from "./LocationSearch";
import withUtilities from "../hooks/withUtilities";

const sessionIcons = {
  1: <IconPerson />,
  3: <IconOnline />,
  2: <IconTelephone />,
  4: <IconEmail />,
};

const clientGroupIcons = {
  1: { src: "/static/icons/Man.png", width: 20 },
  2: { src: "/static/icons/Group.png", width: 40 },
  3: { src: "/static/icons/Couple.png", width: 20 },
  4: { src: "/static/icons/Families.png", width: 35 },
  5: { src: "/static/icons/Couple.png", width: 20 },
  6: { src: "/static/icons/Couple.png", width: 20 },
};

const days = ["M", "T", "W", "T", "F", "S", "S"];

const isMulti = ["languages", "practiceAreas", "practitionerModalities"];

const labels = {
  isLGBTQAffirmative: "Is LGBTQ+ Affirmative",
  livedLGBTQExperience: "Lived LGBTQ+ experience",
  minAveragePrice: "Min avg price",
  maxAveragePrice: "Max avg price",
  freeIntroductoryCall: "Free intro call",
  practitionerName: "Therapist name",
  location: "Location",
  availability: "Availability",
  supervisor: "Supervisor",
};

let searchTimeout;

const SearchForm = ({
  onSubmit,
  onUrlItemUpdate,
  initialValues = null,
  initialLocation = null,
  locationText = "",
  defaultData = null,
}) => {
  const [initialLoad, setInitialLoad] = useState(false);
  const utilities = useUtilityData((utilityData) => utilityData);
  const { boxShadow, onScrollHandler, scrollIndicator } = useScrollWithShadow();
  const [searchQuery, setSearchQuery] = useState({
    sessionTypes: utilities.sessionTypes,
    clientGroups: utilities.clientGroups,
    practiceAreas: utilities.therapistPracticeAreas,
    practitionerModalities: utilities.therapistPractitionerModalities,
    gender: utilities.genders,
    languages: utilities.languages,
    minAveragePrice: null,
    maxAveragePrice: null,
    isLGBTQAffirmative: null,
    livedLGBTQExperience: null,
    freeIntroductoryCall: null,
    ageGroups: utilities.ageGroups,
    religion: utilities.religions,
    healthInsurers: utilities.healthInsurers,
    ethnicity: utilities.ethnicities,
    availability: [
      [false, false, false, false, false, false, false],
      [false, false, false, false, false, false, false],
      [false, false, false, false, false, false, false],
      [false, false, false, false, false, false, false],
      [false, false, false, false, false, false, false],
    ],
    practitionerName: "",
    location: null,
    distance: 1000000,
    disabledFriendly: null,
    supervisor: false,
  });

  const doSearch = useCallback(
    (query, orderOverride = null) => {
      setSearchQuery(query);

      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      // Do search automatically after half a second
      searchTimeout = setTimeout(() => {
        if (onSubmit) {
          const search = {};
          for (const [key, value] of Object.entries(query)) {
            if (value === null || value === "") {
              continue;
            }

            if (key === "availability") {
              let usesAvailability = false;
              for (const time of value) {
                if (usesAvailability) {
                  break;
                }
                for (const day of time) {
                  if (day) {
                    usesAvailability = true;
                    break;
                  }
                }
              }

              if (usesAvailability) {
                search.availability = value;
              }
              continue;
            }

            if (Array.isArray(value)) {
              const values = value.filter((item) => item.selected);
              if (values.length === 0) {
                continue;
              }
              search[key] = values;
              continue;
            }

            search[key] = value;
          }

          onSubmit(search, 0, orderOverride, searchQuery);
        }
      }, 1000);
    },
    [searchQuery, onSubmit]
  );

  const resetFilters = useCallback(
    (clearInitial = false) => {
      if (!initialValues || !utilities) {
        return;
      }

      let modalities = utilities.therapistPractitionerModalities;
      if (initialValues.therapistPractitionerModalities) {
        for (const modality of initialValues.therapistPractitionerModalities) {
          modalities = modalities.map((item) =>
            item.value === modality.value ? { ...item, selected: true } : item
          );
        }
      }

      let practiceAreas = utilities.therapistPracticeAreas;
      if (initialValues.therapistPracticeAreas) {
        for (const modality of initialValues.therapistPracticeAreas) {
          practiceAreas = practiceAreas.map((item) =>
            item.value === modality.value ? { ...item, selected: true } : item
          );
        }
      }

      let baseQuery;

      if (clearInitial) {
        baseQuery = {
          sessionTypes: utilities.sessionTypes,
          clientGroups: utilities.clientGroups,
          practiceAreas: utilities.therapistPracticeAreas,
          practitionerModalities: utilities.therapistPractitionerModalities,
          gender: utilities.genders,
          languages: utilities.languages,
          minAveragePrice: null,
          maxAveragePrice: null,
          isLGBTQAffirmative: null,
          livedLGBTQExperience: null,
          freeIntroductoryCall: null,
          ageGroups: utilities.ageGroups,
          religion: utilities.religions,
          healthInsurers: utilities.healthInsurers,
          ethnicity: utilities.ethnicities,
          availability: [
            [false, false, false, false, false, false, false],
            [false, false, false, false, false, false, false],
            [false, false, false, false, false, false, false],
            [false, false, false, false, false, false, false],
            [false, false, false, false, false, false, false],
          ],
          practitionerName: "",
          location: null,
          distance: 1000000,
          disabledFriendly: null,
          supervisor: false,
        };

        if (onUrlItemUpdate) {
          onUrlItemUpdate("all", null);
        }
      } else {
        baseQuery = {
          sessionTypes: utilities.sessionTypes.map((item) => ({
            ...item,
            selected: initialValues.sessionTypes?.includes(item.value) ?? false,
          })),
          clientGroups: utilities.clientGroups.map((item) => ({
            ...item,
            selected: initialValues.clientGroups?.includes(item.value) ?? false,
          })),
          practiceAreas: practiceAreas,
          practitionerModalities: modalities,
          gender: utilities.genders.map((item) => ({
            ...item,
            selected: initialValues.genders?.includes(item.value) ?? false,
          })),
          languages: utilities.languages.map((item) => ({
            ...item,
            selected: initialValues.languages?.includes(item.value) ?? false,
          })),
          minAveragePrice: initialValues.minAveragePrice ?? null,
          maxAveragePrice: initialValues.maxAveragePrice ?? null,
          isLGBTQAffirmative: initialValues.isLGBTQAffirmative ?? null,
          livedLGBTQExperience: initialValues.livedLGBTQExperience ?? null,
          freeIntroductoryCall: initialValues.freeIntroductoryCall ?? null,
          ageGroups: utilities.ageGroups.map((item) => ({
            ...item,
            selected: initialValues.ageGroups?.includes(item.value) ?? false,
          })),
          religion:
            utilities.religions?.map((item) => ({
              ...item,
              selected: initialValues.religions?.includes(item.value) ?? false,
            })) ?? [],
          healthInsurers: utilities.healthInsurers.map((item) => ({
            ...item,
            selected:
              initialValues.healthInsurers?.includes(item.value) ?? false,
          })),
          ethnicity:
            utilities.ethnicities?.map((item) => ({
              ...item,
              selected:
                initialValues.ethnicities?.includes(item.value) ?? false,
            })) ?? [],
          availability: initialValues.availability ?? [
            [false, false, false, false, false, false, false],
            [false, false, false, false, false, false, false],
            [false, false, false, false, false, false, false],
            [false, false, false, false, false, false, false],
            [false, false, false, false, false, false, false],
          ],
          practitionerName: initialValues.practitionerName ?? "",
          location: initialValues.location ?? null,
          distance: 1000000,
          disabledFriendly: initialValues.disabledFriendly ?? null,
          seed: initialValues.seed ?? null,
          supervisor: initialValues.supervisor ?? null,
        };
      }

      doSearch(baseQuery);
    },
    [doSearch, initialValues, utilities]
  );

  useEffect(() => {
    if (initialValues && !initialLoad) {
      setInitialLoad(true);
      resetFilters();
    }
  }, [initialValues, initialLoad]);

  const updateItem = (key, value, selected = true) => {
    const tmpQuery = { ...searchQuery };

    if (key === "availability") {
      if (value !== null) {
        const availabilities = [...tmpQuery.availability];
        const row = availabilities[value.time];
        row[value.day] = selected;
        availabilities[value.time] = row;
        tmpQuery.availability = availabilities;
      } else {
        tmpQuery.availability = [
          [false, false, false, false, false, false, false],
          [false, false, false, false, false, false, false],
          [false, false, false, false, false, false, false],
          [false, false, false, false, false, false, false],
          [false, false, false, false, false, false, false],
        ];
      }
      doSearch(tmpQuery);
      return;
    }

    if (key === "minMax") {
      const min = value[0] === 0 ? null : value[0];
      const max = value[1] === 100 ? null : value[1];
      tmpQuery.minAveragePrice = min;
      tmpQuery.maxAveragePrice = max;
      doSearch(tmpQuery);
      return;
    }

    if (key === "location") {
      if (!selected && onUrlItemUpdate) {
        onUrlItemUpdate("location", null);
      }
      tmpQuery[key] = selected ? value : null;
      doSearch(tmpQuery, "distance");
      return;
    }

    if (key === "practitionerModalities" && onUrlItemUpdate) {
      const item = tmpQuery[key].filter(
        (item) => item.value === parseInt(value, 10)
      )[0].label;
      onUrlItemUpdate("modality", item);
    }

    if (Array.isArray(searchQuery[key])) {
      if (isMulti.includes(key)) {
        tmpQuery[key] = tmpQuery[key].map((item) =>
          item.value === parseInt(value, 10) ? { ...item, selected } : item
        );
      } else {
        tmpQuery[key] = tmpQuery[key].map((item) =>
          item.value === parseInt(value, 10)
            ? { ...item, selected }
            : { ...item, selected: false }
        );
      }

      doSearch(tmpQuery);
      return;
    }

    tmpQuery[key] = selected ? value : null;
    doSearch(tmpQuery);
  };

  const selectedOptions = useMemo(() => {
    if (!searchQuery) {
      return [];
    }

    let options = [];

    for (const [key, value] of Object.entries(searchQuery)) {
      if (key === "distance" || key === "seed") {
        continue;
      }

      if (key === "availability") {
        let usesAvailability = false;
        for (const time of searchQuery.availability) {
          if (usesAvailability) {
            break;
          }
          for (const day of time) {
            if (day) {
              options.push({ key, value: null, label: "Availability" });
              usesAvailability = true;
              break;
            }
          }
        }
        continue;
      }

      if (!Array.isArray(value)) {
        if (!!value && value !== "") {
          options.push({ key, value, label: labels[key] });
        }
        continue;
      }
      const selected = value.filter((item) => item.selected);
      if (selected.length > 0) {
        options = options.concat(selected.map((item) => ({ ...item, key })));
      }
    }

    return options;
  }, [searchQuery]);

  const selectedEthnicity = useMemo(() => {
    const option = selectedOptions.filter((item) => item.key === "ethnicity");
    if (option.length > 0) {
      return option[0];
    }
    return null;
  }, [selectedOptions]);

  const selectedReligion = useMemo(() => {
    const option = selectedOptions.filter((item) => item.key === "religion");
    if (option.length > 0) {
      return option[0];
    }
    return null;
  }, [selectedOptions]);

  const selectedInsurance = useMemo(() => {
    const option = selectedOptions.filter(
      (item) => item.key === "healthInsurers"
    );
    if (option.length > 0) {
      return option[0];
    }
    return null;
  }, [selectedOptions]);

  return (
    <div
      onScroll={onScrollHandler}
      style={{ boxShadow }}
      className="show-bar flex flex-col w-full gap-6 overflow-auto overflow-y-scroll h-full sm:max-h-[calc(100vh_-_200px)]"
    >
      <div className="border-b px-4 py-6 flex justify-between items-center">
        <h3 className="font-bold text-xl">Filter your search</h3>
        <Button
          onClick={() => resetFilters(true)}
          className="underline text-sm"
        >
          Reset
        </Button>
      </div>
      <div className="flex flex-col w-full px-4 gap-6">
        {selectedOptions.length > 0 && (
          <div className="flex flex-col gap-2 sticky top-0 bg-white p-4 pb-6 -m-4 border-b z-[100]">
            <h4 className="font-bold text-lg">Your filters</h4>
            <div className="w-full flex flex-wrap gap-2">
              {selectedOptions.map((option) => (
                <Button
                  onClick={() => {
                    updateItem(option.key, option.value, false);
                    if (option.key === "location") {
                      onUrlItemUpdate("location", null);
                    }
                  }}
                  className="px-4 py-2 bg-brand-alt flex gap-2 text-white items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition"
                  key={`selected_${option.key}_${option.value}`}
                >
                  <span className="text-white">
                    {option.key === "location" ? locationText : option.label}
                  </span>
                  <MdOutlineClose />
                </Button>
              ))}
            </div>
          </div>
        )}
        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle>What's worrying you?</SearchItemTitle>
          <Select
            value={null}
            onChange={(e) => updateItem("practiceAreas", e.target.value, true)}
            className="w-full appearance-none border border-gray-300 text-gray-300 p-2 rounded-lg"
          >
            <option value={null}>Select an option</option>
            {searchQuery.practiceAreas.map((practiceArea) =>
              practiceArea.selected ? null : (
                <option
                  key={`practiceArea_${practiceArea.value}`}
                  value={practiceArea.value}
                >
                  {practiceArea.label}
                </option>
              )
            )}
          </Select>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle>Types of therapy</SearchItemTitle>
          <Select
            value={null}
            onChange={(e) => {
              updateItem("practitionerModalities", e.target.value, true);
            }}
            className="w-full appearance-none border border-gray-300 text-gray-300 p-2 rounded-lg"
          >
            <option value={null}>Select a therapy type</option>
            {searchQuery.practitionerModalities.map((modality) =>
              modality.selected ? null : (
                <option
                  key={`modality_${modality.value}`}
                  value={modality.value}
                >
                  {modality.label}
                </option>
              )
            )}
          </Select>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle tooltip="Please select one option. If you would like online sessions but still want to work with a therapist local to you, put an in-person location and look for further information on the therapist's profile">
            Session format
          </SearchItemTitle>
          <div className="flex flex-wrap justify-stretch gap-1.5">
            {searchQuery.sessionTypes.map((type) => (
              <Button
                onClick={() =>
                  updateItem("sessionTypes", type.value, !type.selected)
                }
                className={`px-4 py-2 ${type.selected ? "bg-brand-alt" : "bg-brand-off"} flex flex-1 justify-center gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
                key={`sessionType_${type.value}`}
              >
                <div
                  className={`${type.selected ? "text-white" : "text-brand-text"} transition`}
                >
                  {sessionIcons[type.value]}
                </div>
                <p
                  className={`${type.selected ? "text-white" : "text-brand-text"} text-sm font-bold transition`}
                >
                  {type.label}
                </p>
              </Button>
            ))}
          </div>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle tooltip="Find therapists closest to your location">
            Location
          </SearchItemTitle>
          <div className="flex justify-stretch w-full">
            <LocationSearch
              initialLocation={initialLocation}
              onChangeLocation={(name, location) => {
                onUrlItemUpdate("location", name);
                updateItem("location", location);
              }}
            />
          </div>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle tooltip="Only show therapists who identify as a specific gender">
            Therapist gender
          </SearchItemTitle>
          <div className="flex flex-wrap gap-1.5">
            {searchQuery.gender.map((gender) => (
              <Button
                onClick={() =>
                  updateItem("gender", gender.value, !gender.selected)
                }
                className={`px-4 py-2 ${gender.selected ? "bg-brand-alt" : "bg-brand-off"} flex flex-1 justify-center gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
                key={`gender_${gender.value}`}
              >
                <p
                  className={`${gender.selected ? "text-white" : "text-brand-text"} text-sm font-bold transition`}
                >
                  {gender.label}
                </p>
              </Button>
            ))}
          </div>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle tooltip="Therapists must specialise working with the selected group">
            Client groups
          </SearchItemTitle>
          <div className="flex flex-wrap gap-1.5">
            {searchQuery.clientGroups.map((type) => (
              <Button
                onClick={() =>
                  updateItem("clientGroups", type.value, !type.selected)
                }
                className={`px-4 py-2 ${type.selected ? "bg-brand-alt" : "bg-brand-off"} flex flex-1 w-max justify-center gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
                key={`clientGroup_${type.value}`}
              >
                <div className="h-5 flex items-center min-w-[20px]">
                  <Image
                    className={`brightness-0 ${type.selected ? "invert" : "invert-0"} transition-all`}
                    alt={type.label}
                    src={clientGroupIcons[type.value].src}
                    height={20}
                    width={clientGroupIcons[type.value].width}
                  />
                </div>
                <p
                  className={`${type.selected ? "text-white" : "text-brand-text"} text-sm font-bold transition`}
                >
                  {type.label}
                </p>
              </Button>
            ))}
          </div>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle>Availability</SearchItemTitle>
          <div className="flex flex-col w-full">
            <div className="grid grid-cols-[110px_1fr]">
              <div />
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, i) => (
                  <p
                    key={`weekDay_${i}`}
                    className="text-sm font-bold text-center h-[30px]"
                  >
                    {day}
                  </p>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-[110px_1fr]">
              <div className="flex flex-col gap-2.5">
                {utilities.availability.map((label, i) =>
                  label && label !== "" ? (
                    <p className="font-bold text-sm py-1" key={`timeName_${i}`}>
                      {label}
                    </p>
                  ) : null
                )}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {searchQuery.availability.map((availSet, id) =>
                  availSet.map((avail, day) => (
                    <Button
                      key={`day_${id}_${day}`}
                      onClick={() =>
                        updateItem("availability", { time: id, day }, !avail)
                      }
                      className={`text-white text-sm ${avail ? "bg-brand" : "bg-brand-off"} opacity-1 hover:opacity-[0.7] rounded-[5px] flex justify-center items-center py-1 px-3 cursor-pointer`}
                    >
                      <p className="absolute">{avail ? "\u2714" : ""}</p>
                    </Button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle>Session price</SearchItemTitle>
          <div className="flex w-full px-4">
            <RangeSlider
              colorScheme="green"
              defaultValue={[0, 100]}
              onChange={(e) => {
                updateItem("minMax", e);
              }}
            >
              <RangeSliderTrack>
                <RangeSliderFilledTrack />
              </RangeSliderTrack>
              <RangeSliderThumb index={0} className="relative">
                <div className="absolute top-4 rounded-full text-sm font-bold">
                  £{searchQuery.minAveragePrice ?? 0}
                </div>
              </RangeSliderThumb>
              <RangeSliderThumb index={1}>
                <div className="absolute top-4 rounded-full text-sm font-bold">
                  £{searchQuery.maxAveragePrice ?? "100+"}
                </div>
              </RangeSliderThumb>
            </RangeSlider>
          </div>
          <Checkbox
            className="mt-6"
            colorScheme="green"
            isChecked={searchQuery.freeIntroductoryCall}
            onChange={(e) =>
              updateItem("freeIntroductoryCall", e.target.checked)
            }
          >
            <p>Free brief initial phone call?</p>
          </Checkbox>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle>Languages</SearchItemTitle>
          <Select
            value={null}
            onChange={(e) => updateItem("languages", e.target.value, true)}
            className="w-full appearance-none border border-gray-300 text-gray-300 p-2 rounded-lg"
          >
            <option value={null}>Select a language</option>
            {searchQuery.languages.map((language) =>
              language.selected ? null : (
                <option
                  key={`language_${language.value}`}
                  value={language.value}
                >
                  {language.label}
                </option>
              )
            )}
          </Select>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle>Therapist name</SearchItemTitle>
          <Input
            onChange={(e) => updateItem("practitionerName", e.target.value)}
            name="therapist_name"
            type="text"
            className="w-full appearance-none border border-gray-300 placeholder:text-gray-300 p-2 rounded-lg"
            placeholder="Enter a name"
          />
        </div>

        <div className="w-full flex flex-col gap-2">
          <SearchItemTitle tooltip="For use by therapists looking for professional supervision">
            Supervisor
          </SearchItemTitle>
          <div className="w-full flex flex-row items-center gap-2">
            <input
              type="checkbox"
              id="supervisor"
              name="supervisor"
              checked={searchQuery.supervisor}
              onChange={(e) =>
                updateItem("supervisor", e.target.checked, e.target.checked)
              }
            />
            <label for="supervisor"> Offers supervision to therapists</label>
          </div>
        </div>

        <div className="bg-gray-300 w-full h-[1px]" />

        <Disclosure as="div" className="w-full max-w-md pb-6">
          {({ open }) => (
            <>
              <DisclosureButton className="group flex w-full items-center justify-between">
                <span className="text-lg font-bold text-black group-data-[hover]:text-black/50 transition">
                  Advanced search
                </span>
                <FaChevronDown className="size-5 fill-black group-data-[hover]:fill-black/50 group-data-[open]:rotate-180 transition-all" />
              </DisclosureButton>
              <div className="overflow-hidden">
                <AnimatePresence>
                  {open && (
                    <DisclosurePanel
                      static
                      as={motion.div}
                      initial={{ opacity: 0, y: -24 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -24 }}
                      transition={{ duration: 0.2, ease: easeOut }}
                      className="origin-top"
                    >
                      <div className="flex flex-col w-full gap-6">
                        <div className="w-full flex flex-col gap-2">
                          <Checkbox
                            isChecked={searchQuery.isLGBTQAffirmative}
                            onChange={(e) =>
                              updateItem("isLGBTQAffirmative", e.target.checked)
                            }
                            className="mt-6"
                            colorScheme="green"
                          >
                            <Tooltip
                              hasArrow
                              label="Only show therapists that have specifically stated that they are LGBTQ+ affirmative"
                            >
                              <p>Is LGBTQ+ affirmative</p>
                            </Tooltip>
                          </Checkbox>
                          <Checkbox
                            isChecked={searchQuery.livedLGBTQExperience}
                            onChange={(e) =>
                              updateItem(
                                "livedLGBTQExperience",
                                e.target.checked
                              )
                            }
                            className="mt-6"
                            colorScheme="green"
                          >
                            <Tooltip
                              hasArrow
                              label="Only show therapists that have specifically stated that they have lived LGBTQ+ experience"
                            >
                              <p>LGBTQ+ lived experience</p>
                            </Tooltip>
                          </Checkbox>
                        </div>

                        <div className="bg-gray-300 w-full h-[1px]" />

                        <div className="w-full flex flex-col gap-2">
                          <SearchItemTitle>Therapist age group</SearchItemTitle>
                          <div className="flex flex-wrap gap-1.5 justify-stretch">
                            {searchQuery.ageGroups.map((ageGroup) => (
                              <Button
                                onClick={() =>
                                  updateItem(
                                    "ageGroups",
                                    ageGroup.value,
                                    !ageGroup.selected
                                  )
                                }
                                className={`px-4 py-2 ${ageGroup.selected ? "bg-brand-alt" : "bg-brand-off"} flex-1 justify-center flex gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
                                key={`ageGroup_${ageGroup.value}`}
                              >
                                <p
                                  className={`${ageGroup.selected ? "text-white" : "text-brand-text"} text-sm font-bold transition`}
                                >
                                  {ageGroup.label}
                                </p>
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div className="bg-gray-300 w-full h-[1px]" />

                        <div className="w-full flex flex-col gap-2">
                          <SearchItemTitle>Therapist ethnicity</SearchItemTitle>
                          <div className="flex flex-wrap gap-1.5">
                            <Select
                              name="ethnicity"
                              onChange={(e) =>
                                updateItem("ethnicity", e.target.value, true)
                              }
                              className={`w-full appearance-none border border-gray-300 ${selectedEthnicity ? "text-black" : "text-gray-300"} p-2 rounded-lg`}
                            >
                              <option value={null}>
                                {selectedEthnicity
                                  ? selectedEthnicity.label
                                  : "Select an ethnicity"}
                              </option>
                              {searchQuery.ethnicity.map((ethnicity) =>
                                ethnicity.selected ? null : (
                                  <option
                                    key={`ethnicity_${ethnicity.value}`}
                                    value={ethnicity.value}
                                  >
                                    {ethnicity.label}
                                  </option>
                                )
                              )}
                            </Select>
                          </div>
                        </div>

                        <div className="bg-gray-300 w-full h-[1px]" />

                        <div className="w-full flex flex-col gap-2">
                          <SearchItemTitle>Therapist religion</SearchItemTitle>
                          <div className="flex flex-wrap gap-1.5">
                            <Select
                              name="religion"
                              onChange={(e) =>
                                updateItem("religion", e.target.value, true)
                              }
                              className={`w-full appearance-none border border-gray-300 ${selectedReligion ? "text-black" : "text-gray-300"} p-2 rounded-lg`}
                            >
                              <option value={null}>
                                {selectedReligion
                                  ? selectedReligion.label
                                  : "Select a religion"}
                              </option>
                              {searchQuery.religion.map((religion) =>
                                religion.selected ? null : (
                                  <option
                                    key={`religion_${religion.value}`}
                                    value={religion.value}
                                  >
                                    {religion.label}
                                  </option>
                                )
                              )}
                            </Select>
                          </div>
                        </div>

                        <div className="bg-gray-300 w-full h-[1px]" />

                        <div className="w-full flex flex-col gap-2">
                          <SearchItemTitle tooltip="Show only therapists that work with specified insurers">
                            My health insurance
                          </SearchItemTitle>
                          <div className="flex flex-wrap gap-1.5">
                            <Select
                              name="healthInsurer"
                              onChange={(e) =>
                                updateItem(
                                  "healthInsurers",
                                  e.target.value,
                                  true
                                )
                              }
                              className={`w-full appearance-none border border-gray-300 ${selectedInsurance ? "text-black" : "text-gray-300"} p-2 rounded-lg`}
                            >
                              <option value={null}>
                                {selectedInsurance
                                  ? selectedInsurance.label
                                  : "Select an insurance"}
                              </option>
                              {searchQuery.healthInsurers.map(
                                (healthInsurer) =>
                                  healthInsurer.selected ? null : (
                                    <option
                                      key={`healthInsurer_${healthInsurer.value}`}
                                      value={healthInsurer.value}
                                    >
                                      {healthInsurer.label}
                                    </option>
                                  )
                              )}
                            </Select>
                          </div>
                        </div>
                      </div>
                    </DisclosurePanel>
                  )}
                </AnimatePresence>
              </div>
            </>
          )}
        </Disclosure>
      </div>
      {scrollIndicator}
    </div>
  );
};

export default SearchForm;
